import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ApiError } from '../../abstract/api-error';
import { AuthApiService } from '../../core/api-services/auth-api.service';
import { AuthService } from '../../core/services/auth.service';
import { clearableFieldValue } from '../../core/utils/drone-request-utils';
import { ToastService } from '../../core/services/toast.service';
import { FormInputComponent } from 'src/app/shared/components/form-input/form-input.component';
import { Auth } from '@angular/fire/auth';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { UsStateSelectComponent } from 'src/app/shared/components/us-state-select/us-state-select.component';
import { GoogleMapsModule } from '@angular/google-maps';

/**
 * Profile Page Component
 *
 * Displays and allows editing of the current user's profile information.
 */
/** Profile fields the user may leave empty; emptying one clears it on the hub. */
const OPTIONAL_PROFILE_FIELDS = ['phone', 'dateOfBirth', 'address', 'city', 'state', 'zipCode'] as const;
type OptionalProfileField = typeof OPTIONAL_PROFILE_FIELDS[number];

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, FormInputComponent, MaxLengthDirective, UsStateSelectComponent, GoogleMapsModule],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit {
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private auth = inject(Auth);
  private authApiService = inject(AuthApiService);

  profileForm!: FormGroup;
  isLoading = false;
  isSaving = false;
  isEditMode = false;
  phoneVerified = false;
  emailVerified = false;

  // User data
  userEmail = '';
  userDisplayName = '';
  userPhone = '';

  pendingCoordinates: google.maps.LatLngLiteral | null = null;
  savingCoordinates = false;
  lookupError = '';
  saveError = '';

  mapOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    mapTypeId: 'hybrid',
  };

  /** The optional fields as loaded, so a save can tell an emptied field (send `null`) from an untouched one. */
  private loadedOptionalFields: Record<string, string> = {};

  ngOnInit(): void {
    this.initializeForm();
    this.loadProfile();

    if (history.state?.verified) {
      this.toastService.success('Phone number verified');
    }
  }

  verifyPhone(): void {
    // Older profile saves stored bare national digits; Firebase needs E.164.
    try {
      const phone = parsePhoneNumberWithError(this.userPhone, 'US').number;
      this.router.navigate(['/verify/phone'], { state: { phone, next: '/profile' } });
    } catch {
      this.toastService.error('Your saved phone number is invalid. Message an admin to update it');
    }
  }

  /**
   * Initialize the profile form
   */
  private initializeForm(): void {
    this.profileForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phone: [{ value: '', disabled: true }],
      dateOfBirth: [''],
      address: [''],
      city: [''],
      state: [''],
      zipCode: [''],
      latitude: [null, [Validators.min(-90), Validators.max(90)]],
      longitude: [null, [Validators.min(-180), Validators.max(180)]]
    });
  }

  /**
   * Load user profile data
   */
  private async loadProfile(): Promise<void> {
    this.isLoading = true;

    try {
      // Get current user info from auth service
      const user = this.authService.getCurrentUser();
      console.log(user);
      if (user) {
        this.userEmail = user.email || '';
        this.userDisplayName = user.displayName || '';

        // Parse display name into first/last
        if (user.displayName) {
          const names = user.displayName.split(' ');
          this.profileForm.patchValue({
            firstName: names[0] || '',
            lastName: names.slice(1).join(' ') || ''
          });
        }
      }

      // Load profile from API
      const { profile } = await this.authApiService.getCurrentUser();

      if (profile) {
        this.profileForm.patchValue({
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          phone: profile.phone || '',
          dateOfBirth: profile.dateOfBirth ? profile.dateOfBirth.split('T')[0] : '',
          address: profile.address || '',
          city: profile.city || '',
          state: profile.state || '',
          zipCode: profile.zipCode || '',
          latitude: profile.latitude ?? null,
          longitude: profile.longitude ?? null
        });
        this.emailVerified = profile.emailVerified === true;
        this.phoneVerified = !!this.auth.currentUser?.phoneNumber;
        this.userPhone = profile.phone || '';
        this.loadedOptionalFields = this.optionalFieldValues();
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Toggle edit mode
   */
  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;

    if (!this.isEditMode) {
      // Reset form if canceling edit
      this.loadProfile();
    }
  }

  /**
   * Save profile changes
   */
  async saveProfile(): Promise<void> {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;

    try {
      const formData = this.profileForm.value;

      // Rejects with an ApiError (message = the hub's error) when the hub refuses the update.
      await this.authApiService.updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        ...this.changedOptionalFields(),
        latitude: formData.latitude,
        longitude: formData.longitude,
      });

      this.toastService.success('Profile updated successfully');
      this.isEditMode = false;
      this.lookupError = '';

      // Refresh the auth token to get updated display name
      await this.authService.refreshToken();
    } catch (error: unknown) {
      console.error('Error saving profile:', error);
      this.toastService.error(ApiError.fromHttpError(error).errors[0] ?? 'Failed to save profile');
    } finally {
      this.isSaving = false;
    }
  }

  /** The current values of the optional profile fields. */
  private optionalFieldValues(): Record<string, string> {
    const values: Record<string, string> = {};
    for (const key of OPTIONAL_PROFILE_FIELDS) {
      values[key] = this.profileForm.value[key] ?? '';
    }
    return values;
  }

  /**
   * The optional fields to send: a changed value as entered, `null` for a field the user emptied, and
   * nothing for an untouched field (see `clearableFieldValue`).
   */
  private changedOptionalFields(): Partial<Record<OptionalProfileField, string | null>> {
    const current = this.optionalFieldValues();
    const changed: Partial<Record<OptionalProfileField, string | null>> = {};
    for (const key of OPTIONAL_PROFILE_FIELDS) {
      const value = clearableFieldValue(current[key], this.loadedOptionalFields[key]);
      if (value !== undefined) {
        changed[key] = value;
      }
    }
    return changed;
  }

  /**
   * Navigate back to dashboard
   */
  goBack(): void {
    this.router.navigate(['/dashboard']);
  }

  async lookupCoordinates() {
    this.lookupError = '';

    try {
      const coords = await this.authApiService.geocode(
        this.profileForm.value.address,
        this.profileForm.value.city,
        this.profileForm.value.state,
        this.profileForm.value.zipCode
      );

      if (coords) {
       this.pendingCoordinates = {
         lat: coords.lat,
         lng: coords.lng
       };
      } else {
       this.lookupError = 'Failed to find coordinates for the given address';
      }

    } catch (error: unknown) {
      console.error('Error looking up coordinates:', error);
      this.lookupError = ApiError.fromHttpError(error).errors[0] ?? 'Error looking up coordinates';
    }

  }

  async confirmedCoordinates(): Promise<void> {
    if (!this.pendingCoordinates) return;
    const { lat, lng } = this.pendingCoordinates;
    this.savingCoordinates = true;
    this.saveError = '';
    try {
      await this.authApiService.updateProfile({ latitude: lat, longitude: lng });
      this.profileForm.patchValue({ latitude: lat, longitude: lng });
      this.pendingCoordinates = null;
    } catch (error: unknown) {
      this.saveError = ApiError.fromHttpError(error).errors[0] ?? 'Failed to save coordinates';
    } finally {
      this.savingCoordinates = false;
    }
  }

  declinedCoordinates(): void {
    if (this.savingCoordinates) return;
    this.pendingCoordinates = null;
    this.saveError = '';
  }
}
